import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { FREE_DELIVERY_THRESHOLD } from "../data/products";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, deliveryFee, total, count } = useCart();
  const navigate = useNavigate();

  const remaining = Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal);

  const goCheckout = () => {
    setOpen(false);
    navigate("/cart");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[140] h-full w-full max-w-md bg-[#0A0A0A] border-l border-[#1c1c1c] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#1c1c1c]">
              <div className="flex items-center gap-3">
                <ShoppingBag size={18} className="text-[#E6DFD4]" />
                <h2 className="font-display text-2xl tracking-wide uppercase text-[#F5F5F5]">Your Cart ({count})</h2>
              </div>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="text-[#A3A3A3] hover:text-[#E6DFD4]">
                <X size={24} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <ShoppingBag size={40} className="text-[#2c2c2c]" />
                <p className="text-[#A3A3A3]">Your cart is empty.</p>
                <button onClick={() => { setOpen(false); navigate("/"); document.getElementById("products")?.scrollIntoView({ behavior: "smooth" }); }} className="text-[#E6DFD4] text-xs tracking-[0.2em] uppercase font-bold border-b border-[#E6DFD4] pb-1">
                  Browse Peptides
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {items.map((it) => (
                    <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 border-b border-[#161616] pb-5">
                      <div className="h-20 w-16 shrink-0 bg-[#111] border border-[#1c1c1c] overflow-hidden">
                        <img src={it.vial} alt={it.name} className="h-full w-full object-contain p-1" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between gap-2">
                          <h3 className="font-display text-lg tracking-wide uppercase text-[#F5F5F5] leading-none">{it.name}</h3>
                          <button data-testid={`cart-remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="text-[#737373] hover:text-red-400">
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="text-sm text-[#E6DFD4] mt-1">AED {it.price}</div>
                        <div className="mt-3 flex items-center gap-3">
                          <button data-testid={`cart-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity - 1)} className="h-7 w-7 flex items-center justify-center border border-[#2c2c2c] text-[#E6DFD4] hover:bg-[#1a1a1a]">
                            <Minus size={13} />
                          </button>
                          <span className="text-sm text-[#F5F5F5] w-5 text-center">{it.quantity}</span>
                          <button data-testid={`cart-inc-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity + 1)} className="h-7 w-7 flex items-center justify-center border border-[#2c2c2c] text-[#E6DFD4] hover:bg-[#1a1a1a]">
                            <Plus size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-[#1c1c1c] px-6 py-5 space-y-3">
                  {remaining > 0 ? (
                    <p className="text-xs text-[#A3A3A3]">Add <span className="text-[#E6DFD4] font-bold">AED {remaining}</span> more for free UAE delivery.</p>
                  ) : (
                    <p className="text-xs text-[#E6DFD4] font-bold">You've unlocked free UAE delivery.</p>
                  )}
                  <div className="flex justify-between text-sm text-[#A3A3A3]"><span>Subtotal</span><span>AED {subtotal}</span></div>
                  <div className="flex justify-between text-sm text-[#A3A3A3]"><span>Delivery</span><span>{deliveryFee === 0 ? "Free" : `AED ${deliveryFee}`}</span></div>
                  <div className="flex justify-between font-display text-2xl text-[#F5F5F5] pt-1"><span>Total</span><span>AED {total}</span></div>
                  <button
                    data-testid="cart-checkout-button"
                    onClick={goCheckout}
                    className="w-full mt-2 bg-[#E6DFD4] text-black py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors duration-300"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
